import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmojiPickerButton } from "@/components/common/EmojiPickerButton";
import { FileAttachment } from "@/components/common/FileAttachment";
import { FormattingToolbar } from "@/components/common/FormattingToolbar";
import { MentionSuggestions } from "@/components/common/MentionSuggestions";
import { TextEditor } from "@/components/common/TextEditor";
import { Button } from "@/components/ui/button";
import { POST_MAX_FILE_SIZE } from "@/constants";
import { useFileAttachment } from "@/hooks/useFileAttachment";
import { useFormatting } from "@/hooks/useFormatting";
import { useEditorLinkHover } from "@/hooks/useEditorLinkHover";
import { useEditorSelection } from "@/hooks/useEditorSelection";
import { useMention } from "@/hooks/useMention";
import { usePasteHandler } from "@/hooks/usePasteHandler";
import type { MentionCandidate, User } from "@/lib/humhub-api/types";
import { convertTextToMarkdown } from "@/lib/utils/markdownUtils";

// インライン画像貼り付け時の制限値
const MAX_INLINE_IMAGE_COUNT = 5;
const MAX_INLINE_IMAGE_TOTAL_BYTES = 8 * 1024 * 1024;

interface EditorAlertsProps {
  imageError: string | null;
  lengthError: string | null;
}

const Alert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-500">
    {children}
  </div>
);

const EditorAlerts: React.FC<EditorAlertsProps> = ({
  imageError,
  lengthError,
}) => {
  if (!imageError && !lengthError) {
    return null;
  }

  return (
    <>
      {imageError && <Alert>{imageError}</Alert>}
      {lengthError && <Alert>{lengthError}</Alert>}
    </>
  );
};

interface EditorFooterProps {
  isVisible: boolean;
  showFormatting: boolean;
  formattingState: {
    isBold: boolean;
    isItalic: boolean;
    isLink: boolean;
    isCode: boolean;
    isBlockquote: boolean;
    isHeading1: boolean;
    isHeading2: boolean;
  };
  onToggleBold: () => void;
  onToggleItalic: () => void;
  onApplyLink: (url: string) => void;
  onRemoveLink: () => void;
  onBeforeLinkEdit: () => void;
  onToggleCode: () => void;
  onToggleBlockquote: () => void;
  onToggleHeading1: () => void;
  onToggleHeading2: () => void;
  onAttachClick?: () => void;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  submitButtonLabel?: string;
  canSubmit: boolean;
  disabled?: boolean;
  emojiPicker?: React.ReactNode;
  onLinkPopoverOpenChange?: (open: boolean) => void;
}

const EditorFooter: React.FC<EditorFooterProps> = ({
  isVisible,
  showFormatting,
  formattingState,
  onToggleBold,
  onToggleItalic,
  onApplyLink,
  onRemoveLink,
  onBeforeLinkEdit,
  onToggleCode,
  onToggleBlockquote,
  onToggleHeading1,
  onToggleHeading2,
  onAttachClick,
  fileInputRef,
  onFileChange,
  onSubmit,
  submitButtonLabel,
  canSubmit,
  disabled,
  emojiPicker,
  onLinkPopoverOpenChange,
}) => {
  if (!isVisible) {
    return null;
  }

  return (
    <div className="mt-3 flex items-center justify-between border-t border-custom-border pt-2">
      {showFormatting && (
        <FormattingToolbar
          isBold={formattingState.isBold}
          isItalic={formattingState.isItalic}
          isLink={formattingState.isLink}
          isCode={formattingState.isCode}
          isBlockquote={formattingState.isBlockquote}
          isHeading1={formattingState.isHeading1}
          isHeading2={formattingState.isHeading2}
          onToggleBold={onToggleBold}
          onToggleItalic={onToggleItalic}
          onApplyLink={onApplyLink}
          onRemoveLink={onRemoveLink}
          onBeforeLinkEdit={onBeforeLinkEdit}
          onToggleCode={onToggleCode}
          onToggleBlockquote={onToggleBlockquote}
          onToggleHeading1={onToggleHeading1}
          onToggleHeading2={onToggleHeading2}
          onAttachClick={onAttachClick}
          fileInputRef={fileInputRef}
          onFileChange={onFileChange}
          emojiPicker={emojiPicker}
          onLinkPopoverOpenChange={onLinkPopoverOpenChange}
        />
      )}
      <Button
        variant="submit"
        size="sm"
        onClick={onSubmit}
        disabled={!canSubmit || disabled}
      >
        {submitButtonLabel}
      </Button>
    </div>
  );
};

export interface BaseInputEditorVisibilityProps {
  alwaysVisible?: boolean;
}

interface BaseInputEditorProps extends BaseInputEditorVisibilityProps {
  placeholder?: string;
  submitButtonLabel?: string;
  showFormatting?: boolean;
  onSubmit?: (content: string, files?: File[]) => Promise<void>;
  className?: string;
  disabled?: boolean;
  maxFileSize?: number;
  allowedFileTypes?: string[];
  onMentionTrigger?: (query: string) => Promise<User[]>;
  mentionUsers?: User[];
  isLoadingMentionUsers?: boolean;
  context: "post" | "chat";
  autoFocus?: boolean;
  maxLength?: number;
  onFocusChange?: (isFocused: boolean) => void;
}

export const BaseInputEditor: React.FC<BaseInputEditorProps> = ({
  placeholder,
  submitButtonLabel,
  showFormatting = true,
  onSubmit,
  className = "",
  disabled,
  maxFileSize,
  allowedFileTypes,
  onMentionTrigger,
  mentionUsers = [],
  isLoadingMentionUsers = false,
  context,
  autoFocus = false,
  maxLength,
  alwaysVisible = false,
  onFocusChange,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [content, setContent] = useState("");
  const [imageError, setImageError] = useState<string | null>(null);
  const [lengthError, setLengthError] = useState<string | null>(null);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const hasAutoFocusedRef = useRef(false);
  const { savedSelectionRef, restoreSelection, saveCurrentSelection } =
    useEditorSelection({ editorRef });
  const { hoveredLink, handlePointerOver, handlePointerOut } =
    useEditorLinkHover({ editorRef, containerRef });

  // autoFocus機能
  useEffect(() => {
    if (
      autoFocus &&
      editorRef.current &&
      !disabled &&
      !hasAutoFocusedRef.current
    ) {
      hasAutoFocusedRef.current = true;
      editorRef.current.focus();
      setIsFocused(true);
      onFocusChange?.(true);
    }
  }, [autoFocus, disabled, onFocusChange]);

  useEffect(() => {
    return () => {
      hasAutoFocusedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!autoFocus) {
      hasAutoFocusedRef.current = false;
    }
  }, [autoFocus]);

  const mentionHook = useMention({
    onFetchUsers: async (query) => {
      if (onMentionTrigger) {
        return onMentionTrigger(query);
      }
      return mentionUsers;
    },
    context,
    maxSuggestions: 5,
    includeStudyGroup: true,
  });

  const formattingHook = useFormatting({ editorRef });

  const fileAttachmentHook = useFileAttachment({
    maxFileSize,
    allowedFileTypes,
    onFilesChange: (files) => {
      if (files.length > 0) {
        setIsFocused(true);
        onFocusChange?.(true);
      }
    },
  });

  const validateContent = useCallback((): {
    isValid: boolean;
    contentToSubmit: string;
    lengthError?: string;
  } => {
    if (!editorRef.current) return { isValid: false, contentToSubmit: "" };

    const contentToSubmit = convertTextToMarkdown(
      editorRef.current,
      showFormatting,
    );
    const hasContent = contentToSubmit.trim().length > 0;
    if (!hasContent) {
      return { isValid: false, contentToSubmit };
    }

    if (maxLength && contentToSubmit.length > maxLength) {
      return {
        isValid: false,
        contentToSubmit,
        lengthError: t("common.maxLengthExceeded", { maxLength }),
      };
    }

    return { isValid: true, contentToSubmit };
  }, [maxLength, showFormatting, t]);

  const submitContent = useCallback(
    async (contentToSubmit: string) => {
      await onSubmit?.(
        contentToSubmit,
        fileAttachmentHook.attachedFiles.length > 0
          ? fileAttachmentHook.attachedFiles
          : undefined,
      );
    },
    [fileAttachmentHook, onSubmit],
  );

  const resetEditorState = useCallback(() => {
    if (!editorRef.current) return;

    editorRef.current.innerHTML = "";
    editorRef.current.textContent = "";
    setContent("");
    fileAttachmentHook.clearFiles();
    setIsFocused(false);
    onFocusChange?.(false);
    editorRef.current.blur();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setImageError(null);
    setLengthError(null);
    mentionHook.hideMentionSuggestions();
    formattingHook.resetFormatState();
  }, [fileAttachmentHook, formattingHook, mentionHook, onFocusChange]);

  const { handlePaste } = usePasteHandler({
    editorRef,
    maxInlineImageCount: MAX_INLINE_IMAGE_COUNT,
    maxInlineImageTotalBytes: MAX_INLINE_IMAGE_TOTAL_BYTES,
    maxFileSize: maxFileSize ?? POST_MAX_FILE_SIZE,
    translate: t,
    onImageError: (message) => setImageError(message),
    onInlineImageAdd: (file) => fileAttachmentHook.addFiles([file]),
    getCurrentAttachmentCount: () => fileAttachmentHook.attachedFiles.length,
    getCurrentAttachmentTotalBytes: () =>
      fileAttachmentHook.totalAttachmentSize,
  });

  const handleBlur = (e: React.FocusEvent) => {
    if (fileAttachmentHook.isFileSelectionInProgress) {
      return;
    }

    if (isEmojiPickerOpen || isLinkPopoverOpen) {
      return;
    }

    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }

    if (fileAttachmentHook.attachedFiles.length > 0) {
      return;
    }

    if (editorRef.current && editorRef.current.textContent?.trim() === "") {
      setIsFocused(false);
      onFocusChange?.(false);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const newContent = target.textContent || "";

    if (newContent !== content) {
      setContent(newContent);
    }

    // 画像エラーをクリア
    if (imageError) {
      setImageError(null);
    }

    if (maxLength && editorRef.current) {
      const validation = validateContent();
      setLengthError(validation.lengthError ?? null);
    } else {
      setLengthError(null);
    }

    formattingHook.updateFormatState();

    const selection = window.getSelection();
    mentionHook.handleTextInput(selection, editorRef);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSubmit();
      return;
    }

    const mentionHandled = mentionHook.handleKeyDown(e);
    if (mentionHandled) {
      if (
        e.key === "Enter" &&
        mentionHook.filteredMentionCandidates.length > 0
      ) {
        const selectedCandidate =
          mentionHook.filteredMentionCandidates[
            mentionHook.selectedMentionIndex
          ];
        handleMentionSelect(selectedCandidate);
      }
      return;
    }
  };

  const handleSubmit = async () => {
    const {
      isValid,
      contentToSubmit,
      lengthError: validationError,
    } = validateContent();
    if (!isValid) return;

    setLengthError(validationError ?? null);

    try {
      await submitContent(contentToSubmit);
      resetEditorState();
    } catch (error) {
      console.error(`Error submitting ${context}:`, error);
    }
  };

  const handleMentionSelect = (item: MentionCandidate) => {
    mentionHook.insertMention(item, editorRef);

    setTimeout(() => {
      if (editorRef.current) {
        const newContent = editorRef.current.textContent || "";
        setContent(newContent);

        // Check content length using the same logic as other input handlers
        checkContentLength();
      }
    }, 0);
  };

  const checkContentLength = useCallback(() => {
    if (!editorRef.current) return;

    if (!maxLength) {
      setLengthError(null);
      return;
    }

    setTimeout(() => {
      if (editorRef.current) {
        const contentToCheck = convertTextToMarkdown(
          editorRef.current,
          showFormatting,
        );

        if (contentToCheck.length > maxLength) {
          setLengthError(t("common.maxLengthExceeded", { maxLength }));
        } else {
          setLengthError(null);
        }
      }
    }, 0);
  }, [maxLength, showFormatting, t]);

  const handleToggleBold = useCallback(() => {
    formattingHook.toggleBold();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleToggleItalic = useCallback(() => {
    formattingHook.toggleItalic();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleApplyLink = useCallback(
    (url: string) => {
      restoreSelection();
      formattingHook.applyLink(url);
      checkContentLength();
    },
    [restoreSelection, formattingHook, checkContentLength],
  );

  const handleRemoveLink = useCallback(() => {
    restoreSelection();
    formattingHook.removeLink();
    checkContentLength();
  }, [restoreSelection, formattingHook, checkContentLength]);

  const handleToggleCode = useCallback(() => {
    formattingHook.toggleCode();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleToggleBlockquote = useCallback(() => {
    formattingHook.toggleBlockquote();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleToggleHeading1 = useCallback(() => {
    formattingHook.toggleHeading1();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleToggleHeading2 = useCallback(() => {
    formattingHook.toggleHeading2();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const insertEmojiAtCaret = (emoji: string) => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    const selection = window.getSelection();
    if (!selection) return;

    selection.removeAllRanges();
    if (savedSelectionRef.current) {
      selection.addRange(savedSelectionRef.current);
    } else {
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.addRange(range);
    }

    const activeRange = selection.getRangeAt(0);
    activeRange.deleteContents();

    const textNode = document.createTextNode(emoji);
    activeRange.insertNode(textNode);

    const afterRange = document.createRange();
    afterRange.setStartAfter(textNode);
    afterRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(afterRange);
    savedSelectionRef.current = afterRange.cloneRange();

    const updatedContent = editorRef.current.textContent || "";
    setContent(updatedContent);
    setIsFocused(true);
    checkContentLength();
  };

  const isExpanded =
    alwaysVisible ||
    isFocused ||
    fileAttachmentHook.attachedFiles.length > 0 ||
    isEmojiPickerOpen ||
    isLinkPopoverOpen;
  const isEmpty = content.trim() === "";
  const canSubmit = !isEmpty && !lengthError;

  return (
    <div
      ref={containerRef}
      className={`base-input-editor rounded-tr-lg border-2 border-custom-border bg-white p-3 shadow-sm ${className} relative`}
      onBlur={handleBlur}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <TextEditor
        ref={editorRef}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => {
          setIsFocused(true);
          onFocusChange?.(true);
        }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className="min-h-6 leading-6"
      />

      {hoveredLink && (
        <div
          className="absolute z-50 rounded bg-slate-800 px-2 py-1 text-xs text-white shadow"
          style={{
            top: hoveredLink.top,
            left: hoveredLink.left,
            maxWidth: "300px",
            wordWrap: "break-word",
          }}
        >
          {hoveredLink.url}
        </div>
      )}

      <EditorAlerts imageError={imageError} lengthError={lengthError} />

      <FileAttachment
        files={fileAttachmentHook.attachedFiles}
        fileError={fileAttachmentHook.fileError}
        onRemoveFile={fileAttachmentHook.removeFile}
      />

      <MentionSuggestions
        isVisible={mentionHook.showMentionSuggestions}
        position={mentionHook.mentionPosition}
        candidates={mentionHook.filteredMentionCandidates || []}
        selectedIndex={mentionHook.selectedMentionIndex}
        isLoading={isLoadingMentionUsers}
        onSelect={handleMentionSelect}
        onClose={mentionHook.hideMentionSuggestions}
        suggestionsRef={mentionHook.mentionSuggestionsRef}
      />

      <EditorFooter
        isVisible={isExpanded}
        showFormatting={showFormatting}
        formattingState={{
          isBold: formattingHook.isBold,
          isItalic: formattingHook.isItalic,
          isLink: formattingHook.isLink,
          isCode: formattingHook.isCode,
          isBlockquote: formattingHook.isBlockquote,
          isHeading1: formattingHook.isHeading1,
          isHeading2: formattingHook.isHeading2,
        }}
        onToggleBold={handleToggleBold}
        onToggleItalic={handleToggleItalic}
        onApplyLink={handleApplyLink}
        onRemoveLink={handleRemoveLink}
        onBeforeLinkEdit={saveCurrentSelection}
        onToggleCode={handleToggleCode}
        onToggleBlockquote={handleToggleBlockquote}
        onToggleHeading1={handleToggleHeading1}
        onToggleHeading2={handleToggleHeading2}
        onAttachClick={fileAttachmentHook.handleAttachClick}
        fileInputRef={fileAttachmentHook.fileInputRef}
        onFileChange={fileAttachmentHook.handleFileChange}
        onSubmit={handleSubmit}
        submitButtonLabel={submitButtonLabel}
        canSubmit={canSubmit}
        disabled={disabled}
        emojiPicker={
          <EmojiPickerButton
            onBeforeOpen={saveCurrentSelection}
            onSelect={insertEmojiAtCaret}
            onOpenChange={setIsEmojiPickerOpen}
          />
        }
        onLinkPopoverOpenChange={setIsLinkPopoverOpen}
      />
    </div>
  );
};
